import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Paper, Typography, useTheme } from "@mui/material";
import { ViewByClaimType } from "@/types";
import { useChartPalette, getFormattedValue } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type MonthlyStat = {
  name: string;
  claims: number;
  amount: number;
};

type Props = {
  data: MonthlyStat[];
  viewBy: ViewByClaimType;
  isFullscreen: boolean;
};

// "YYYY-MM" -> "Aug 25" (abbreviated month + 2-digit year), matching the policy
// dashboard's month-axis formatting (RenewalTimelineChart).
const formatYearMonth = (ym?: string) => {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  if (!year || !month) return ym;
  return new Date(Number(year), Number(month) - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

const MonthTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { payload: MonthlyStat }[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{formatYearMonth(label)}</Typography>
      <Typography variant="body2">{p.claims.toLocaleString()} claims incurred</Typography>
      <Typography variant="body2">{formatUSD(p.amount)} paid</Typography>
    </Paper>
  );
};

const ClaimsByMonthLineChart = ({ data, viewBy, isFullscreen }: Props) => {
  const chartPalette = useChartPalette();
  const theme = useTheme();
  const isAmountViewByClaimType = viewBy === ViewByClaimType.CLAIM_AMOUNT;
  const dataKey = isAmountViewByClaimType ? "amount" : "claims";

  if (!data.length) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <AreaChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="claimsMonthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={chartPalette[4]} stopOpacity={0.5} />
            <stop offset="95%" stopColor={chartPalette[4]} stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          tickFormatter={formatYearMonth}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          tickFormatter={(value: number) => getFormattedValue(value, viewBy)}
        />
        <Tooltip
          content={(props) => (
            <MonthTooltip
              active={props.active}
              label={props.label !== undefined && props.label !== null ? String(props.label) : undefined}
              payload={props.payload as unknown as { payload: MonthlyStat }[] | undefined}
            />
          )}
        />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={chartPalette[4]}
          strokeWidth={2}
          fill="url(#claimsMonthFill)"
          dot={false}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default ClaimsByMonthLineChart;
